import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from 'all-in-one-react-keycloak';
import { CLAIM_STATUSES, useViewClaimDetails } from 'all-in-one-shared-api';

import { isLocaleEnglish } from 'utils/languageUtils';
import { formatClaimDetails, FormattedClaimDetails } from 'utils/formatClaimDetails';
import { useClaimPropertiesContext } from 'contexts/claimProperties';
import { useCachedClaimProperties } from 'hooks';
import { GenericResult, Spinner } from 'components';
import { FullWidthSpace } from 'components/styledComponents';

import ClaimAlert from '../ClaimAlert';
import {
  ClaimInformationTable,
  ClaimOverviewCard,
  PartialClaimInformation,
  ShortfallInformation,
  SupplementaryDocuments,
} from '../index';

interface CLaimDetailsProps {
  claimId: string;
  sequenceNo?: number;
  isShortfall?: boolean;
}

export const ClaimDetails = ({ claimId, sequenceNo, isShortfall }: CLaimDetailsProps): JSX.Element => {
  const { keycloak, initialized } = useKeycloak();
  const { t, i18n } = useTranslation();
  const [inited, setInited] = useState(false);

  const { shouldFetch, data, mutate, isLoading, error } = useViewClaimDetails(
    { name: 'useViewClaimDetails', params: { claimId, sequenceNo, isShortfall } },
    keycloak.token || '',
    claimId,
    sequenceNo,
    {},
    isShortfall,
  );

  useEffect(() => {
    const ready = initialized && !!keycloak.authenticated && !!keycloak.token;
    shouldFetch(ready);

    if (!inited && ready) setInited(true);
  }, [inited, initialized, keycloak.authenticated, keycloak.token, shouldFetch]);

  const { key: claimPropertiesKey } = useClaimPropertiesContext();
  const { data: claimProperties } = useCachedClaimProperties(claimPropertiesKey);

  const formattedClaimDetails = useMemo<FormattedClaimDetails | undefined>(
    () => (data ? formatClaimDetails(data, claimProperties ?? {}, isLocaleEnglish(i18n.language)) : undefined),
    [data, claimProperties, i18n.language],
  );

  if (!inited || isLoading) {
    // Note(Anson Kan): to prevent showing the error before start fetching
    return <Spinner />;
  }

  if (error || !data || !formattedClaimDetails) {
    return (
      <GenericResult
        status="error"
        title={t('claims.notFound.title')}
        defaultTo={isShortfall ? '/claims/shortfall' : '/claims'}
        defaultLabel={t('claims.notFound.goBack')}
      />
    );
  }

  return (
    <FullWidthSpace direction="vertical" size="large">
      <ClaimAlert claimStatus={formattedClaimDetails.claimStatus} reasons={formattedClaimDetails.reasons} />

      <ClaimOverviewCard
        claimData={data}
        claimId={data.claimId}
        sequenceNo={data.sequenceNo}
        settlementFile={formattedClaimDetails.settlementFile}
        ctcRequest={formattedClaimDetails.ctcRequest}
        referenceNo={formattedClaimDetails.referenceNo || ''}
        claimType={formattedClaimDetails.claimType}
        diagnosisName={formattedClaimDetails.diagnosisName}
        claimStatus={formattedClaimDetails.claimStatus}
        doctorName={formattedClaimDetails.doctorName}
        statusHistories={formattedClaimDetails.statusHistories}
        refreshClaimData={mutate}
        editAllowed={data.editAllowed}
        claimTypeDescription={formattedClaimDetails.claimTypeDescription}
        shortfallFlag={data.shortfallFlag}
      />

      {data.claimStatus === CLAIM_STATUSES.SHORTFALL && (
        <ShortfallInformation
          shortfallAmount={data.shortfallAmount}
          daysOverdue={data.daysOverdue}
          claimId={data.claimId}
          sequenceNo={data.sequenceNo}
          btnDisabled={data.shortfallFlag === 'Y'}
        />
      )}

      <ClaimInformationTable
        sequenceNo={data.sequenceNo}
        referenceNo={formattedClaimDetails.referenceNo}
        claimId={formattedClaimDetails.claimId}
        claimStatus={formattedClaimDetails.claimStatus}
        claimType={formattedClaimDetails.claimType}
        patientName={formattedClaimDetails.patientName}
        treatmentDate={formattedClaimDetails.treatmentDate}
        doctorName={formattedClaimDetails.doctorName}
        claimTypeDescription={formattedClaimDetails.claimTypeDescription}
        diagnosisName={formattedClaimDetails.diagnosisName}
        receipts={formattedClaimDetails.receipts}
        paymentMethod={
          isLocaleEnglish(i18n.language) ? formattedClaimDetails.paymentMethod : formattedClaimDetails.paymentMethod_zh
        }
        paidAmount={data.paidAmount}
        networkClaim={data.networkClaim}
      />

      {!formattedClaimDetails.fullClaim && (
        <PartialClaimInformation
          otherInsurerName={formattedClaimDetails.otherInsurerName}
          otherClaimAmount={formattedClaimDetails.otherClaimAmount}
          anotherPolicyNumber={formattedClaimDetails.anotherPolicyNumber}
          anotherMemberId={formattedClaimDetails.anotherMemberId}
          prevClaimFiles={formattedClaimDetails.prevClaimFiles}
        />
      )}

      <SupplementaryDocuments
        claimId={formattedClaimDetails.claimId || formattedClaimDetails.referenceNo}
        sequenceNo={formattedClaimDetails.sequenceNo}
        claimStatus={formattedClaimDetails.claimStatus}
        receiptFiles={formattedClaimDetails.receiptFiles}
        refLetters={formattedClaimDetails.refLetters}
        otherDocs={formattedClaimDetails.otherDocs}
        receiptTitle={formattedClaimDetails.receiptTitle}
        attachmentTitle={formattedClaimDetails.attachmentTitle}
        referenceNo={formattedClaimDetails.referenceNo}
      />
    </FullWidthSpace>
  );
};
